import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Показывается вместо детей после падения. Без него рисуется дефолтный экран. */
  fallback?: ReactNode
  /** Сброс, когда меняется значение: у роутера это путь — уход на другую
   *  страницу должен снимать ошибку, иначе приложение залипает навсегда. */
  resetKey?: unknown
}

interface State {
  hasError: boolean
}

/**
 * Ловит падения рендера в поддереве.
 *
 * До 2026-08-21 границы не было вовсе (записано как известный пробел в
 * CLAUDE.md), и любое исключение в ленивой странице обнуляло весь дашборд:
 * пользователь видел белый лист без единого объяснения. Реальный путь такого
 * падения нашёлся на этапе 1 — описание олимпиады с некорректной HTML-сущностью
 * роняло `String.fromCodePoint` прямо в рендере карточки.
 *
 * Границу нельзя написать хуками: React даёт `componentDidCatch` только
 * классовому компоненту.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Сервиса сбора ошибок в проекте нет — пишем в консоль, чтобы падение
    // осталось видимым в devtools и не растворилось молча.
    console.error('[error-boundary] падение рендера:', error, info.componentStack)
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children
    return this.props.fallback ?? null
  }
}
